import Link from "next/link";
import Image from "next/image";
import { Text } from "~/components/Text";
import Icon from "~/assets/icons/Icon";
import classNames from "classnames";
import { useRouter } from "next/router";
import { getRoute } from "~/utils/getRoute";
import {
  ShowAllCardProps,
  SubCategoryCardProps,
  SubCategoryPickerProps,
} from "./types";

import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Mousewheel, FreeMode } from "swiper";
import "swiper/swiper.min.css";
import { getFormattedImageAttributes } from "~/utils/getImageAttributes";

export const ShowAllCard = ({ mainCategory }: ShowAllCardProps) => {
  const { query } = useRouter();
  const active =
    query.categoryId === String(mainCategory.id) && !query.subCategoryId;

  const background = active ? "bg-primary-500" : "bg-secondary-100";
  const color = active ? "text-white-500" : "text-secondary-800";

  return (
    <Link
      href={getRoute("/categories/:categoryId/:categoryName", {
        categoryId: mainCategory.id,
        categoryName: mainCategory.attributes.name,
      })}
    >
      <div className="w-full relative">
        <div
          className={classNames(
            "rounded-md mb-2 w-full aspect-square flex justify-center items-center",
            background,
            color
          )}
        >
          <Icon name="plusSquare" />
        </div>
        <Text
          className={classNames(
            "text-xs md:text-sm leading-3",
            active && "text-primary-500"
          )}
        >
          Vis alt
        </Text>
      </div>
    </Link>
  );
};

export const SubCategoryCard = ({ subCategory }: SubCategoryCardProps) => {
  const image = subCategory.attributes.image.data;
  const subCategoryName = subCategory.attributes.name;

  const { query } = useRouter();
  const mainCategory = subCategory.attributes.mainCategory?.data;

  if (!mainCategory) return null;

  const mainCategoryName = mainCategory.attributes.name;

  const active = query.subCategoryId === String(subCategory.id);

  return (
    <Link
      href={getRoute(
        "/categories/:categoryId/:categoryName/:subCategoryId/:subCategoryName",
        {
          categoryId: mainCategory.id,
          categoryName: mainCategoryName,
          subCategoryId: subCategory.id,
          subCategoryName,
        }
      )}
    >
      <div className="w-full">
        <div className="w-full aspect-square relative rounded-xl overflow-hidden mb-2">
          <Image
            className="object-cover h-full w-full"
            {...getFormattedImageAttributes(image, "small")}
          />
          {active && (
            <div className="absolute w-full h-full flex items-center justify-center bg-primary-500 top-0 left-0 bg-opacity-70 text-white-500">
              <Icon name="eye" />
            </div>
          )}
        </div>
        <Text
          className={classNames(
            "text-xs md:text-sm leading-3",
            active && "text-primary-500"
          )}
        >
          {subCategoryName}
        </Text>
      </div>
    </Link>
  );
};

const slideClass = "max-w-[80px] md:max-w-[120px]";

export const SubCategoryPicker = ({
  mainCategory,
  subCategories,
}: SubCategoryPickerProps) => {
  return (
    <Swiper
      className="mb-32"
      spaceBetween={10}
      breakpoints={{
        768: {
          spaceBetween: 20,
        },
      }}
      wrapperClass="!items-start"
      slidesPerView="auto"
      modules={[A11y, Mousewheel, FreeMode]}
      mousewheel={{ sensitivity: 1 }}
      freeMode={{ enabled: true }}
    >
      <SwiperSlide className={slideClass}>
        <ShowAllCard mainCategory={mainCategory} />
      </SwiperSlide>
      {subCategories.map((subCategory) => (
        <SwiperSlide key={subCategory.id} className={slideClass}>
          <SubCategoryCard subCategory={subCategory} />
        </SwiperSlide>
      ))}
    </Swiper>
  );
};
